import {
  notZeroValidation,
  isPositiveValidation,
  isNumeric,
  getMinimumSelectableIncrementErrorMessage,
  validateMinimumSelectableIncrement,
  isHTML,
  htmlToTextWithEnter,
  requiredValidationForFields,
  requiredValidationForEditor,
  isEmpty,
} from "../../utils/Validator";
import {
  MAX_LENGTH,
  SKU_DRAFT_OPTIONAL_FIELDS,
  VALIDATION_ERROR_MESSAGES,
  RICH_TEXT_EDITOR_FIELD_NAMES,
} from "../../Constant";
import { ErrorListType } from "../CTProductCostSetup/ProductDataType";
import {
  CreateSkuFormValues,
  CreateSkuRequest,
  CreateBmoApprovalFormValues,
  SkuErrorListType,
} from "./type";
import { COST_METRIC } from "./Constants";

export const mapSKuDataWithApi = (
  data: CreateSkuFormValues,
  isEdit = false,
): CreateSkuRequest => {
  return {
    SkuId: data.skuNumber,
    ProductId: data.productId,
    ProductSkuNum: data.ProductSkuNum,
    Name: data.skuShortDescription,
    Description: data.skuLongDescription,
    CostMacroCategoryCd: data.costMacroCategory || "",
    CostMetricCd: data.CostMetricCd || "",
    CurrencyCd: "usd",
    CostMetricCost: data.cost !== "" ? Number(data.cost) : null,
    CostIntervalCd: data.chargingIncrement || "",
    CostIntervalMin:
      data.minimumSelectableIncrement !== ""
        ? Number(data.minimumSelectableIncrement)
        : null,
    CostIntervalMax: 0,
    IsAutoProvisioned:
      data.autoProvision === "1"
        ? true
        : data.autoProvision === "0"
          ? false
          : null,
    IsReturnable:
      data.returnable === "1" ? true : data.returnable === "0" ? false : null,
    StatusCd: data.commercialStatus,
    SkuRelation: data.skuRelation,
    BillingTypeCd: data.billingType || "",
    CostMetricNSR: data.CostMetricNSR != null ? data.CostMetricNSR : null,
    GlobalRiskMgr: data.GlobalRiskMgr,
    LocalRiskMgr: data.LocalRiskMgr,
    Indexation: data.Indexation != null ? data.Indexation : null,
    ProductApprover: data.ProductApprover,
    DeploymentApprover: data.DeploymentApprover,
    isEdit: isEdit,
    ConsumptionMgr: data.ConsumptionMgr,
  };
};

export const mapBmoSKuDataWithApi = (data: CreateBmoApprovalFormValues) => {
  const costMacroCategoryArr: string[] = Array.isArray(data?.costMacroCategory)
    ? data!.costMacroCategory.filter((item) => item !== "all")
    : [];
  return {
    SkuId: data.skuNumber,
    ProductId: data.productId,
    ProductSkuNum: data.ProductSkuNum,
    SkuName: data.skuShortDescription,
    SkuDescription: data.skuLongDescription,
    CostMacroCategoryCd: costMacroCategoryArr,
    CostMetricCd: data.costMetric,
    CurrencyCd: "usd",
    CostMetricCost: Number(data.cost),
    CostIntervalCd: data.chargingIncrement,
    CostIntervalMin: Number(data.minimumSelectableIncrement),
    CostIntervalMax: 0,
    IsAutoProvisioned:
      data.autoProvision === "1"
        ? true
        : data.autoProvision === "0"
          ? false
          : null,
    IsReturnable:
      data.returnable === "1" ? true : data.returnable === "0" ? false : null,
    Indexation: data.Indexation,
    SkuRelation: data.skuRelation,
    BillingTypeCd: data.billingType,
    CostMetricNSR: data.CostMetricNSR,
    GlobalRiskMgr: data?.GlobalRiskMgr,
    LocalRiskMgr: data?.LocalRiskMgr,
    ProductApprover: data?.ProductApprover,
    DeploymentApprover: data?.DeploymentApprover,
    ConsumptionMgr: data.ConsumptionMgr,
    // isEdit: isEdit,
  };
};

const checkMaxLength = (value: string, maxLength: number) => {
  if (value.length > maxLength) {
    return false;
  }
  return value;
};

const strReplace = (str: string, search: string, replace: string) => {
  const regex = new RegExp(search, "g");
  return str.replace(regex, replace);
};

function isInteger(x: number | string): boolean {
  if (typeof x === "string") {
    if (x === "") {
      return false; // Empty string is not an integer
    }
    if (!/^\d+$/.test(x.toString())) {
      return false; // Not a number
    }
    x = parseFloat(x); // Convert string to number
  }
  return Math.round(x) === x;
}
export const validatePercentageRange = (val: any) => {
  if (val === null || val === undefined || val === "") return false;

  const numVal = parseFloat(val);
  return !isNaN(numVal) && numVal >= 0 && numVal <= 100;
};

function isAlphaNumeric(str: string): boolean {
  let code, i, len;
  for (i = 0, len = str.length; i < len; i++) {
    code = str.charCodeAt(i);
    if (
      !(code > 47 && code < 58) && // numeric (0-9)
      !(code > 64 && code < 91) && // upper alpha (A-Z)
      !(code > 96 && code < 123)
    ) {
      // lower alpha (a-z)
      return false;
    }
  }
  return true;
}

export const validateFormFields = (
  name: string,
  value: string,
  errorList: SkuErrorListType,
  setError: React.Dispatch<React.SetStateAction<SkuErrorListType>>,
  statusType?: string,
  formFields?: CreateSkuFormValues,
) => {
  let isError = false;
  const errorObject = errorList[name as keyof typeof errorList];
  const errorObjectMaxLen = errorList[name as keyof typeof errorList] as {
    isError: boolean;
    message: string;
    validation: string[];
    label: string;
    maxLengthLimit: number;
  };
  if (errorObject === undefined) {
    return false;
  }
  const validations = errorObject?.validation;

  const label = strReplace(errorObject?.label, ":", " ");

  if (validations.length === 0) {
    return false;
  }
  if (
    validations.includes("required") &&
    statusType === "sku_draft" &&
    name === "costCategory" &&
    !isEmpty(value)
  ) {
    if (isEmpty(formFields?.costMetric)) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        costMetric: {
          ...prevState["costMetric" as keyof typeof errorList],
          isError: true,
          message: `${COST_METRIC} is a required field`,
          label: COST_METRIC,
        },
      })); // set error to true
      isError = true;
    }
  }
  if (
    validations.includes("required") &&
    !(statusType === "sku_draft" && SKU_DRAFT_OPTIONAL_FIELDS.includes(name))
  ) {
    let isValid = false;
    let hasHtml = false;
    if (RICH_TEXT_EDITOR_FIELD_NAMES.includes(name)) {
      isValid = requiredValidationForEditor(value);
    } else {
      ({ isValid, hasHtml } = requiredValidationForFields(value));
    }
    if (!isValid) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: !hasHtml
            ? `${label} is a required field`
            : `${label} contains invalid characters`,
          label: errorObject?.label,
        },
      })); // set error to true
      isError = true;
    }
  }

  //checking a number validation
  if (!isError && value && validations.includes("isNumber")) {
    if (!isNumeric(Number(value))) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name as keyof SkuErrorListType]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: VALIDATION_ERROR_MESSAGES.MINIMUM_SELECT_INCREMENT_INT_NUM,
          label: errorObject?.label,
        },
      })); // set error to true
      isError = true;
    }
  }
  //checking not zero validation
  if (!isError && value && validations.includes("notZero")) {
    if (!notZeroValidation(Number(value))) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name as keyof SkuErrorListType]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: VALIDATION_ERROR_MESSAGES.MINIMUM_SELECT_INCREMENT_INT_NUM,
          label: errorObject?.label,
        },
      })); // set error to true
      isError = true;
    }
  }

  //checking positive integer validation
  if (!isError && value && validations.includes("isPositive")) {
    if (!isPositiveValidation(Number(value))) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: VALIDATION_ERROR_MESSAGES.MINIMUM_SELECT_INCREMENT_INT_NUM,
          label: errorObject?.label,
        },
      })); // set error to true
      isError = true;
    }
  }

  if (!isError && value && validations.includes("isInteger")) {
    if (!isInteger(value)) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name as keyof SkuErrorListType]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: VALIDATION_ERROR_MESSAGES.MINIMUM_SELECT_INCREMENT_INT_NUM,
          label: errorObject?.label,
        },
      })); // set error to true
      isError = true;
    }
  }

  if (!isError && value && validations.includes("validatePercentageRange")) {
    if (!validatePercentageRange(value)) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name as keyof SkuErrorListType]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: `${label} must be between 0.00 to 100.00`,
          label: errorObject?.label,
        },
      }));
      isError = true;
    }
  }

  if (!isError && value && validations.includes("isAlphaNumeric")) {
    if (!isAlphaNumeric(value)) {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name as keyof ErrorListType]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: `${label} should be a AlphaNumeric`,
          label: errorObject?.label,
        },
      })); // set error to true
      isError = true;
    }
  }
  if (!isError && value && validations && validations.includes("maxLength")) {
    if (isHTML(value)) {
      value = htmlToTextWithEnter(value);
    }
    const isValid = checkMaxLength(
      value,
      errorObjectMaxLen.maxLengthLimit || MAX_LENGTH,
    );
    if (!isValid && value.trim() !== "") {
      setError((prevState: SkuErrorListType) => ({
        ...prevState,
        [name as keyof SkuErrorListType]: {
          ...prevState[name as keyof SkuErrorListType],
          isError: true,
          message: `${label} should not exceed more than ${errorObjectMaxLen?.maxLengthLimit} characters`,
          label: errorObject?.label,
        },
      })); // set error to true
      isError = true;
    }
  }
  const { chargingIncrement, minimumSelectableIncrement } = formFields || {};
  const isMinimumSelectableIncrementField =
    name === "minimumSelectableIncrement";
  const isChargingIncrementField = name === "chargingIncrement";

  // Validate minimum selectable increment for month/week charging intervals
  if (
    ((isMinimumSelectableIncrementField &&
      value &&
      chargingIncrement &&
      ["month", "week"].includes(chargingIncrement)) ||
      (isChargingIncrementField &&
        Number(minimumSelectableIncrement) > 0 &&
        ["month", "week"].includes(value as string))) &&
    !isError
  ) {
    const incrementValue = isMinimumSelectableIncrementField
      ? Number(value)
      : Number(minimumSelectableIncrement);
    const intervalType = (
      isMinimumSelectableIncrementField
        ? chargingIncrement
        : (value as "month" | "week")
    ) as "month" | "week";

    const isValidIncrement = validateMinimumSelectableIncrement(
      incrementValue,
      intervalType,
    );

    setError((prevState) => ({
      ...prevState,
      minimumSelectableIncrement: {
        ...prevState["minimumSelectableIncrement" as keyof SkuErrorListType],
        isError: !isValidIncrement,
        message: isValidIncrement
          ? ""
          : getMinimumSelectableIncrementErrorMessage(intervalType),
      },
    }));

    isError = !isValidIncrement;
  }

  if (isError === false) {
    setError((prevState: SkuErrorListType) => ({
      ...prevState,
      [name as keyof ErrorListType]: {
        ...prevState[name as keyof SkuErrorListType],
        isError: false,
        message: "",
        label: errorObject?.label,
      },
    })); // set error to false
  }
  return isError;
};

const filedsArraySKU = [
  "skuShortDescription",
  "skuLongDescription",
  "costCategory",
  "chargingIncrement",
  "returnable",
  "costMacroCategory",
  "minimumSelectableIncrement",
  "cost",
  "skuRelation",
  "autoProvision",
  "commercialStatus",
  "costMetric",
  "billingType",
  "CostMetricNSR",
  "globalRiskManager",
  "localRiskManager",
  "productApprover",
  "deploymentApprover",
  "consumptionManager",
  "Indexation",
];

export const getEditableFields = (status: string) => {
  if (!status || status === "" || status === undefined || status === null) {
    return [];
  }
  if (status === "sku_draft" || status === "sku_rejected") {
    return filedsArraySKU;
  } else if (status.includes("sku_active")) {
    return [
      "skuShortDescription",
      "skuLongDescription",
      "commercialStatus",
      "billingType",
      "globalRiskManager",
      "localRiskManager",
      "productApprover",
      "deploymentApprover",
      "consumptionManager",
      "Indexation",
    ];
  } else if (status.includes("sku_submitted")) {
    return [
      "globalRiskManager",
      "localRiskManager",
      "productApprover",
      "deploymentApprover",
      "consumptionManager",
      "Indexation",
    ];
  } else if (status === "sku_retired") {
    return [
      "commercialStatus",
      "consumptionManager",
      "globalRiskManager",
      "localRiskManager",
      "productApprover",
      "deploymentApprover",
    ];
  } else {
    return [];
  }
};

export const downloadArtifactFile = (fileData: any): void => {
  const fileContent = fileData?.fileContent;
  const fileName = fileData?.fileName || "artifact";
  const byteCharacters = atob(fileContent);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: fileContent || "application/octet-stream",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};
